import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Instagram, MapPin, Mail } from "lucide-react";
import { useCart } from "../context/CartContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  return (
    <header className="bg-[#F9F6F0] border-b border-[#262626]/10 sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img src="https://customer-assets.emergentagent.com/job_hometown-era/artifacts/4l7b22fo_EF7B67EC-73B3-4703-A6FF-160834DE60CA.png" alt="Hometown Era Cards logo" className="h-14 w-14 object-contain transition-transform group-hover:scale-105" />
            <span className="font-display font-black text-xl tracking-tight text-[#262626] leading-none">
              Hometown Era Card Shop<span className="text-[#D32F2F]">.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-semibold uppercase tracking-wide transition-colors hover:text-[#D32F2F] ${
                  location.pathname === n.to ? "text-[#D32F2F]" : "text-[#262626]"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-button"
              className="relative p-2.5 rounded-full hover:bg-[#262626]/5 transition-colors"
              aria-label="Open cart"
            >
              <ShoppingCart className="w-5 h-5 text-[#262626]" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#D32F2F] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden p-2.5 rounded-full hover:bg-[#262626]/5"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <nav className="md:hidden border-t border-[#262626]/10 bg-[#F9F6F0] px-6 py-4 flex flex-col gap-1" data-testid="mobile-nav">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              className="py-3 text-sm font-semibold uppercase tracking-wide text-[#262626] hover:text-[#D32F2F] border-b border-[#262626]/5 last:border-0"
            >
              {n.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#262626] text-[#F9F6F0] mt-auto" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <span className="font-display font-black text-2xl tracking-tight">
          Hometown Era Cards<span className="text-[#D32F2F]">.</span>
        </span>
        <p className="mt-4 text-sm text-[#F9F6F0]/70 leading-relaxed max-w-xs">
          A family-owned trading card shop where nostalgia meets the next generation of collectors.
        </p>
        <a
          href="https://instagram.com"
          target="_blank"
          rel="noreferrer"
          data-testid="instagram-link"
          className="inline-flex items-center gap-2 mt-5 text-sm font-semibold hover:text-[#D32F2F] transition-colors"
        >
          <Instagram className="w-5 h-5" /> Follow us on Instagram
        </a>
      </div>
      <div>
        <h4 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-[#D32F2F] mb-4">Shop</h4>
        <ul className="space-y-2.5 text-sm text-[#F9F6F0]/80">
          <li><Link to="/products?category=pokemon" className="hover:text-white transition-colors">Pokémon</Link></li>
          <li><Link to="/products?category=sports" className="hover:text-white transition-colors">Sports Cards</Link></li>
          <li><Link to="/products?category=graded" className="hover:text-white transition-colors">Graded Cards</Link></li>
          <li><Link to="/products?category=sealed" className="hover:text-white transition-colors">Sealed Products</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-[#D32F2F] mb-4">Visit & Contact</h4>
        <ul className="space-y-3 text-sm text-[#F9F6F0]/80">
          <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D32F2F]" /> Galt, California</li>
          <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#D32F2F]" /> sales@hometowneracards.com</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-[#F9F6F0]/50">
      © {new Date().getFullYear()} Hometown Era Cards · Family-owned in Galt, CA
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="App flex flex-col min-h-screen">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
